import { Env } from './env'
import LaunchDarkly, {
  LDFlagValue,
  LDFlagSet,
  LDClient,
} from 'launchdarkly-js-client-sdk'
import memoize from 'memoizee'
import axios from 'axios'
import { Observable } from 'rxjs'
import { concatMap, filter, take } from 'rxjs/operators'
import User from 'iam/models/User'
import UserData from 'iam/models/UserData'
import { Id } from 'types/definitions'

/**
 * Memoized toggle client factory
 * Use this throughout the app
 */
export const getFeatureToggleClient = memoize(() => createFeatureToggleClient())

export const initializeToggleClient = memoize(
  async (client: LDClient | LocalLDClient) => {
    await client.waitForInitialization()
  },
)

export const identifyToggleClient = (
  client: LDClient | LocalLDClient,
  currentUser$: Observable<User>,
  getByPublicId: (publicId: Id) => Promise<UserData>,
) => {
  return currentUser$.pipe(
    filter((user) => user.isFullyLoaded),
    concatMap(async (user) => {
      const userData = await getByPublicId(user.publicId)
      if (!user.email.includes('e2e-')) {
        if ('identify' in client) {
          await client.identify({
            key: user.publicId,
            email: user.email,
            custom: {
              workspace_slugs: userData.workspaceSlugs,
            },
          })
        }
      }
    }),
    take(1),
  )
}

/**
 * Factory function that creates the correct feature toggle client
 */
function createFeatureToggleClient() {
  if (Env.LAUNCHDARKLY_CLIENT_SIDE_ID) {
    const anonymousUser = {
      key: 'anonymous',
      anonymous: true,
    }
    const ldClient = LaunchDarkly.initialize(
      Env.LAUNCHDARKLY_CLIENT_SIDE_ID,
      anonymousUser,
    )
    return ldClient
  } else {
    return new LocalLDClient()
  }
}

/**
 * LocalLDClient basically mirrors the main usecases from
 * the LaunchDarkly client. We use this for local development
 * by querying a dev-only route that leverages a local redis
 * feature toggle store used by all other services
 */

export class LocalLDClient {
  constructor(private flagValues: LDFlagSet = {}) {}

  variation(key: string, defaultValue?: LDFlagValue): LDFlagValue {
    const flagExists = Object.prototype.hasOwnProperty.call(
      this.flagValues,
      key,
    )

    if (!flagExists) {
      return defaultValue || false
    }

    return this.flagValues[key]
  }

  allFlags(): LDFlagSet {
    return this.flagValues
  }

  async waitForInitialization() {
    try {
      const fetchedResp = await axios.get('/__featureToggles')
      const fetchedVals = fetchedResp.data
      this.flagValues = fetchedVals
    } catch (err) {
      console.error('Error initializing local LaunchDarkly client', err)
    }
  }
}
